/* eslint-disable no-unused-vars */
import "./User.scss";
import { useEffect, useState } from "react";
import LocationMapModal from "../../components/LocationMapModal/LocationMapModal";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  ProductOrdersColumns,
  UserAddressColumns,
  UserRatesColumns,
} from "../../SourceData";
import UserImg from "./../../assets/user-img.png";
import FactoriesProductsGif from "./../../assets/factories-products-gif.gif";
import AddressLocation from "./../../assets/location-icon.png";
import DeleteUser from "./../../assets/delete.svg";
import ViewUser from "./../../assets/show-user.svg";
import GreenButton from "../../components/GreenButton/GreenButton";
import MessageModals from "../../components/MessageModals/MessageModals";
import { useMediaQuery } from "@mui/material";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import api from "../../utils/api";
import Loader from "../../components/Loader";

const User = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");
  const { id } = useParams();
  const [openDelete, setOpenDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("orders");
  const [currentModalView, setCurrentModalView] = useState("userRates");
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [userRatings, setUserRatings] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  useEffect(() => {
    getUserData();
  }, [id]);

  useEffect(() => {
    // Fetch data based on the current view
    if (view === "orders") {
      getUserBookings();
    } else if (view === "rates") {
      getUserRatings();
    }
    // Address view uses local data from userData, no need for API call
  }, [view, id, page, pageSize]);

  const getUserData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/users/${id}`);

      if (response.data) {
        // Set user data
        setUserData(response.data.user);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getUserBookings = async () => {
    try {
      setViewLoading(true);
      const response = await api.get(
        `/bookings/${id}/user?page=${page}&limit=${pageSize}`
      );

      if (response.data) {
        setUserBookings(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 0);
      }
    } catch (error) {
      // Reset data on error
      setUserBookings([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setViewLoading(false);
    }
  };

  const getUserRatings = async () => {
    try {
      setViewLoading(true);
      const response = await api.get(
        `/ratings/${id}/user?page=${page}&limit=${pageSize}`
      );

      if (response.data) {
        setUserRatings(response.data.data || []);
        // Change this line to look for totalDocuments instead of totalCount if your API returns that
        setTotalCount(
          response.data.totalDocuments || response.data.totalCount || 0
        );
        setTotalPages(response.data.totalPages || 0);
      }
    } catch (error) {
      // Reset data on error
      setUserRatings([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setViewLoading(false);
    }
  };

  const handleOpen = (view, row = null) => {
    if (row) {
      setSelectedRow(row);
    }
    setCurrentModalView(view);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedRow(null);
  };

  const handleOpenMapModal = (address) => {
    setSelectedAddress(address);
    setMapModalOpen(true);
  };

  // Prepare ratings data for DataGrid
  const prepareRatingsData = () => {
    return userRatings?.map((rating, index) => {
      return {
        _id: rating._id,
        id: rating._id || index,
        index: (page - 1) * pageSize + index + 1,
        service:
          rating.bookingId?.services?.map((s) => s.name.ar).join(", ") ||
          "خدمة",
        rating: rating.rating || 0,
        comment: rating.comment || "",
        date: rating.createdAt
          ? new Date(rating.createdAt).toLocaleDateString("ar-SA")
          : "",
      };
    });
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "",
      width: 160,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            <div className="cell-btn">
              {view === "address" ? (
                <img
                  src={AddressLocation}
                  alt="View Location"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenMapModal(params.row);
                  }}
                />
              ) : (
                <img
                  src={ViewUser}
                  alt=""
                  onClick={(e) => {
                    e.stopPropagation();
                    if (view === "rates") {
                      handleOpen("userRates", params.row);
                    } else {
                      navigate(`/orders/${params.row._id}`);
                    }
                  }}
                />
              )}
            </div>
            {view === "rates" && (
              <div
                className="cell-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDelete(true);
                  setSelectedRow(params.row);
                }}
              >
                <img src={DeleteUser} alt="" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const renderOrdersView = () => (
    <div className="container">
      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={ProductOrdersColumns.concat(actionColumn)}
        rows={userBookings.map((user, index) => ({
          ...user,
          // Generate unique id if not present
          id: user._id || index,
          // Add index for numbering
          index: index + 1,
        }))}
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        loading={viewLoading}
        pagination
        paginationMode="server"
        rowCount={totalCount}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page: page - 1 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        onPaginationModelChange={(model) => {
          // Handle page change
          if (model.page !== page - 1) {
            setPage(model.page + 1);
          }

          // Handle page size change
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            setPage(1); // Reset to first page when changing page size
          }
        }}
        getRowClassName={(params) => "data-grid-row"}
        localeText={{
          MuiTablePagination: {
            labelRowsPerPage: "طلب في الصفحة",
          },
          noRowsLabel: "لا توجد بيانات",
        }}
        components={{
          NoRowsOverlay: () => (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                padding: "20px",
                color: "#666",
              }}
            >
              <p>لا توجد بيانات للعرض</p>
            </div>
          ),
        }}
        rowHeight={60}
      />
    </div>
  );

  const renderRatesView = () => (
    <div className="container">
      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={UserRatesColumns.concat(actionColumn)}
        rows={prepareRatingsData()}
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        loading={viewLoading}
        pagination
        paginationMode="server"
        rowCount={totalCount}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page: page - 1 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        onPaginationModelChange={(model) => {
          // Handle page change
          if (model.page !== page - 1) {
            setPage(model.page + 1);
          }

          // Handle page size change
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            setPage(1); // Reset to first page when changing page size
          }
        }}
        getRowClassName={(params) => "data-grid-row"}
        localeText={{
          MuiTablePagination: {
            labelRowsPerPage: "تقييم في الصفحة",
          },
          noRowsLabel: "لا توجد بيانات",
        }}
        components={{
          NoRowsOverlay: () => (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                padding: "20px",
                color: "#666",
              }}
            >
              <p>لا توجد بيانات للعرض</p>
            </div>
          ),
        }}
        rowHeight={60}
      />
    </div>
  );

  const renderAddressView = () => (
    <div className="container">
      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={UserAddressColumns.concat(actionColumn)}
        rows={(userData?.address || [])?.map((row, index) => ({
          ...row,
          id: row._id || index,
          index: index + 1,
        }))}
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        rowHeight={60}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        localeText={{
          MuiTablePagination: {
            labelRowsPerPage: "عنوان في الصفحة",
          },
          noRowsLabel: "لا توجد بيانات",
        }}
        components={{
          NoRowsOverlay: () => (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                padding: "20px",
                color: "#666",
              }}
            >
              <p>لا توجد بيانات للعرض</p>
            </div>
          ),
        }}
      />
    </div>
  );

  const views = {
    orders: renderOrdersView,
    rates: renderRatesView,
    address: renderAddressView,
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="user">
          <div className="user-header">
            <div className="user-info">
              <div className="info-container">
                <p className="title">تفاصيل المستخدم</p>
                <div className="info">
                  <img src={userData?.photo || UserImg} alt="" />
                  <div className="wrapper">
                    <p className="name">{userData?.name || ""}</p>
                    <span className="phone" style={{ direction: "ltr" }}>
                      {userData?.phoneNumber || ""}
                    </span>
                  </div>
                </div>
              </div>

              <Link className="widget">
                <div
                  className="icon-container"
                  style={{ backgroundColor: "#ECF9FF" }}
                >
                  <img
                    src={FactoriesProductsGif}
                    alt=""
                    className="widget-icon"
                  />
                </div>
                <div className="widget-text-container">
                  <p className="number">{userData?.bookingsCount || 0}</p>
                </div>

                <span className="widget-title">الطلبات المكتملة</span>
              </Link>
            </div>

            <div className="btns-container">
              <GreenButton
                text="الطلبات"
                size={isMobile ? "full" : "medium"}
                variant={view === "orders" ? "fill" : "outlined"}
                onClick={() => {
                  setView("orders");
                  setPage(1); // Reset page when changing view
                }}
              />

              <GreenButton
                text="العناوين"
                size={isMobile ? "full" : "small"}
                variant={view === "address" ? "fill" : "outlined"}
                onClick={() => {
                  setView("address");
                }}
              />

              <GreenButton
                text="التقييمات"
                size={isMobile ? "full" : "small"}
                variant={view === "rates" ? "fill" : "outlined"}
                onClick={() => {
                  setView("rates");
                  setPage(1); // Reset page when changing view
                }}
              />
            </div>
          </div>

          {views[view]()}

          <MessageModals
            open={openDelete}
            currentView="delete"
            route={
              view === "rates"
                ? `/ratings/${selectedRow?._id}`
                : `/users/deleteAddress/${selectedRow?._id}`
            }
            deleteMessage={
              view === "rates" ? "تم حذف هذا التقييم بنجاح" : "تم الحذف بنجاح"
            }
            onClose={() => {
              setOpenDelete(false);
              setSelectedRow(null);
            }}
          />

          <SwipeableModals
            open={isOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            currentView={currentModalView}
            rowData={selectedRow}
          />
        </div>
      )}

      <LocationMapModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        address={selectedAddress}
      />
    </>
  );
};

export default User;
